import { useCallback, useEffect, useMemo, useRef } from 'react';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
/** DOM attribute used to tag draggable nodes for event delegation. */
export const NODE_ID_ATTR = 'data-node-id';
/** DOM attribute used to tag node depth for drop-position calculations. */
export const NODE_DEPTH_ATTR = 'data-node-depth';
/** DOM attribute used to tag node position-in-siblings for drop calculations. */
export const NODE_POSITION_ATTR = 'data-node-position';
/** DOM attribute used to tag whether a node is the last among its siblings. */
export const NODE_LAST_SIBLING_ATTR = 'data-last-sibling';
/** DOM attribute used to tag whether a node is drag-disabled. */
export const NODE_DRAG_DISABLED_ATTR = 'data-drag-disabled';
// ---------------------------------------------------------------------------
// Lookup Map construction
// ---------------------------------------------------------------------------
/**
 * Walk the items recursively (supporting both flat and nested shapes) and
 * build O(1) lookup maps. The `children[]` hierarchy is authoritative — we
 * only fall back to `parentId` when no children tree is provided.
 */
export function buildLookupMaps(items) {
    const nodeById = new Map();
    const parentById = new Map();
    const childrenByParentId = new Map();
    const pushChild = (parentId, child) => {
        const bucket = childrenByParentId.get(parentId);
        if (bucket) {
            bucket.push(child);
        }
        else {
            childrenByParentId.set(parentId, [child]);
        }
    };
    const walk = (list, parentId) => {
        list.forEach((item) => {
            nodeById.set(item.id, item);
            parentById.set(item.id, parentId);
            pushChild(parentId, item);
            if (item.children && item.children.length > 0) {
                walk(item.children, item.id);
            }
        });
    };
    // Partition into "roots" (items without a parent entry in our own list).
    // An item is a root when either (a) parentId is nullish, or (b) parentId
    // points to something that is not present in the top-level list (the tree
    // arrived as a pre-nested structure).
    const topLevelIds = new Set(items.map((i) => i.id));
    const roots = items.filter((item) => {
        const pid = item.parentId;
        if (pid === null || pid === undefined)
            return true;
        return !topLevelIds.has(pid);
    });
    // When the caller supplied a flat list (no `children[]` on any root) group
    // them by `parentId` to reconstruct the tree — otherwise honor the nested
    // shape as-is.
    const anyNested = roots.some((r) => r.children && r.children.length > 0);
    if (anyNested || roots.length === items.length) {
        walk(roots, null);
    }
    else {
        // Flat input: build children buckets from parentId relationships.
        const byParent = new Map();
        items.forEach((item) => {
            var _a;
            const pid = (_a = item.parentId) !== null && _a !== void 0 ? _a : null;
            const bucket = byParent.get(pid);
            if (bucket)
                bucket.push(item);
            else
                byParent.set(pid, [item]);
        });
        const recurse = (parentId) => {
            var _a;
            const list = (_a = byParent.get(parentId)) !== null && _a !== void 0 ? _a : [];
            list.forEach((item) => {
                nodeById.set(item.id, item);
                parentById.set(item.id, parentId);
                pushChild(parentId, item);
                recurse(item.id);
            });
        };
        recurse(null);
    }
    return { nodeById, parentById, childrenByParentId };
}
/**
 * Returns true if `childId` is a descendant of (or equal to) `parentId`,
 * walking the `childrenByParentId` map iteratively with a visited guard
 * to prevent infinite loops on malformed data.
 */
export function isDescendant(maps, parentId, childId) {
    if (parentId === childId)
        return true;
    const visited = new Set();
    const queue = [parentId];
    while (queue.length > 0) {
        const current = queue.shift();
        if (visited.has(current))
            continue;
        visited.add(current);
        const children = maps.childrenByParentId.get(current);
        if (!children)
            continue;
        for (let i = 0; i < children.length; i += 1) {
            const child = children[i];
            if (child.id === childId)
                return true;
            queue.push(child.id);
        }
    }
    return false;
}
// ---------------------------------------------------------------------------
// rAF throttle
// ---------------------------------------------------------------------------
/**
 * Throttles a callback so it fires at most once per animation frame. Unlike a
 * time-based debounce, the UI stays in sync with the browser's repaint cadence
 * — ideal for pointer-tracking callbacks such as dragover.
 */
export function useRafThrottle(fn) {
    const rafIdRef = useRef(null);
    const latestArgsRef = useRef(null);
    const fnRef = useRef(fn);
    useEffect(() => {
        fnRef.current = fn;
    }, [fn]);
    useEffect(() => () => {
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
    }, []);
    return useCallback((...args) => {
        latestArgsRef.current = args;
        if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                if (latestArgsRef.current) {
                    fnRef.current(...latestArgsRef.current);
                }
            });
        }
    }, []);
}
// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------
/** Walk up from `target` until we find the nearest element carrying `data-node-id`. */
function findNodeElement(target) {
    if (!(target instanceof Element))
        return null;
    return target.closest(`[${NODE_ID_ATTR}]`);
}
/** Parse a `data-node-id` string back into the original id type. */
function parseNodeId(raw) {
    if (raw === null || raw === '')
        return null;
    const asNumber = Number(raw);
    return Number.isFinite(asNumber) && String(asNumber) === raw ? asNumber : raw;
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
/**
 * Centralises all drag-and-drop logic for `DraggableTreeView`. Returns
 * container-level event handlers for event delegation — no per-node handlers
 * are installed, which is the key performance lever for large trees.
 */
export function useTreeDragDrop({ items, maxDepth, onDragEnd, store }) {
    const lookupMaps = useMemo(() => buildLookupMaps(items), [items]);
    // Keep a ref to avoid re-creating callbacks on every re-render.
    const stateRef = useRef({ lookupMaps, maxDepth, onDragEnd });
    useEffect(() => {
        stateRef.current = { lookupMaps, maxDepth, onDragEnd };
    }, [lookupMaps, maxDepth, onDragEnd]);
    // -------------------------------------------------------------------------
    // Drag start
    // -------------------------------------------------------------------------
    const handleContainerDragStart = useCallback((e) => {
        const nodeEl = findNodeElement(e.target);
        if (!nodeEl)
            return;
        const dragDisabled = nodeEl.getAttribute(NODE_DRAG_DISABLED_ATTR) === 'true';
        if (dragDisabled) {
            e.preventDefault();
            return;
        }
        const nodeId = parseNodeId(nodeEl.getAttribute(NODE_ID_ATTR));
        if (nodeId === null)
            return;
        store.getState().setDraggedItemId(nodeId);
        // Preserve original visual appearance in the drag image (mirrors the
        // behaviour previously implemented in HierarchicalTree.tsx).
        e.dataTransfer.setData('text/plain', String(nodeId));
        e.dataTransfer.effectAllowed = 'move';
        const rect = nodeEl.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;
        const dragImage = nodeEl.cloneNode(true);
        dragImage.style.position = 'absolute';
        dragImage.style.top = '-1000px';
        dragImage.style.left = '-1000px';
        dragImage.style.width = `${rect.width}px`;
        dragImage.style.height = `${rect.height}px`;
        dragImage.style.opacity = '0.8';
        dragImage.style.transform = 'none';
        dragImage.style.pointerEvents = 'none';
        dragImage.style.zIndex = '9999';
        dragImage.classList.remove('tree-node--dragging');
        dragImage.classList.add('tree-node--drag-preview');
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, offsetX, offsetY);
        // Removed from the DOM on the next tick — the browser has already
        // snapshotted the drag image by then.
        setTimeout(() => {
            if (document.body.contains(dragImage)) {
                document.body.removeChild(dragImage);
            }
        }, 0);
    }, [store]);
    // -------------------------------------------------------------------------
    // Drag hover (rAF throttled)
    // -------------------------------------------------------------------------
    const computeDragHover = useCallback((nodeId, nodeDepth, nodePosition, isLastSibling, clientY, nodeRect) => {
        const state = store.getState();
        const draggedId = state.draggedItemId;
        if (draggedId === null)
            return;
        const y = clientY - nodeRect.top;
        const { height } = nodeRect;
        let dropPosition;
        let canDrop = true;
        if (y < height * 0.25) {
            dropPosition = 'before';
        }
        else if (y > height * 0.75) {
            dropPosition = 'after';
        }
        else {
            dropPosition = 'inside';
            canDrop = nodeDepth < stateRef.current.maxDepth - 1 && String(draggedId) !== String(nodeId);
        }
        // Extra guards when hovering over the dragged node itself.
        if (String(draggedId) === String(nodeId)) {
            if (dropPosition === 'inside') {
                canDrop = false;
            }
            else if (dropPosition === 'before' && (nodePosition === 0 || nodePosition === undefined)) {
                canDrop = false;
            }
            else if (dropPosition === 'after' && isLastSibling) {
                canDrop = false;
            }
            else {
                canDrop = true;
            }
        }
        state.setDragHover(nodeId, dropPosition, canDrop);
    }, [store]);
    const throttledDragHover = useRafThrottle(computeDragHover);
    const handleContainerDragOver = useCallback((e) => {
        var _a;
        const nodeEl = findNodeElement(e.target);
        if (!nodeEl)
            return;
        // Must `preventDefault` to allow drop.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const nodeId = parseNodeId(nodeEl.getAttribute(NODE_ID_ATTR));
        if (nodeId === null)
            return;
        const depth = Number((_a = nodeEl.getAttribute(NODE_DEPTH_ATTR)) !== null && _a !== void 0 ? _a : 0);
        const rawPosition = nodeEl.getAttribute(NODE_POSITION_ATTR);
        const position = rawPosition === null ? undefined : Number(rawPosition);
        const isLastSibling = nodeEl.getAttribute(NODE_LAST_SIBLING_ATTR) === 'true';
        const rect = nodeEl.getBoundingClientRect();
        throttledDragHover(nodeId, depth, position, isLastSibling, e.clientY, rect);
    }, [throttledDragHover]);
    // -------------------------------------------------------------------------
    // Drag leave
    // -------------------------------------------------------------------------
    const handleContainerDragLeave = useCallback((e) => {
        const nodeEl = findNodeElement(e.target);
        if (!nodeEl)
            return;
        // Only clear if the cursor is truly leaving the node (not just entering
        // a child element). `relatedTarget` is the element we're moving into.
        const related = e.relatedTarget;
        if (related instanceof Node && nodeEl.contains(related))
            return;
        const nodeId = parseNodeId(nodeEl.getAttribute(NODE_ID_ATTR));
        if (nodeId === null)
            return;
        store.getState().clearDragHover(nodeId);
    }, [store]);
    // -------------------------------------------------------------------------
    // Drag end
    // -------------------------------------------------------------------------
    const handleContainerDragEnd = useCallback(() => {
        store.getState().reset();
    }, [store]);
    // -------------------------------------------------------------------------
    // Drop
    // -------------------------------------------------------------------------
    const handleContainerDrop = useCallback((e) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const nodeEl = findNodeElement(e.target);
        if (!nodeEl)
            return;
        e.preventDefault();
        const state = store.getState();
        const draggedId = state.draggedItemId;
        const hoveredId = state.hoveredNodeId;
        const { dropPosition } = state;
        // Always clear drag state, regardless of whether we commit.
        const finalize = () => state.reset();
        if (draggedId === null || hoveredId === null || dropPosition === null) {
            finalize();
            return;
        }
        const { lookupMaps: maps, onDragEnd: cb } = stateRef.current;
        const draggedItem = maps.nodeById.get(draggedId);
        const targetItem = maps.nodeById.get(hoveredId);
        if (!draggedItem || !targetItem || !cb) {
            finalize();
            return;
        }
        // Depth-limit check: `inside` drops add one level, before/after preserve depth.
        const targetDepth = Number((_a = nodeEl.getAttribute(NODE_DEPTH_ATTR)) !== null && _a !== void 0 ? _a : 0);
        const newDepth = dropPosition === 'inside' ? targetDepth + 1 : targetDepth;
        if (newDepth >= stateRef.current.maxDepth) {
            finalize();
            return;
        }
        // Prevent dropping on self or any descendant.
        if (isDescendant(maps, draggedId, hoveredId)) {
            finalize();
            return;
        }
        // Compute new parent + position.
        let newParentId;
        let newPosition;
        if (dropPosition === 'inside') {
            newParentId = hoveredId;
            newPosition = ((_b = maps.childrenByParentId.get(hoveredId)) !== null && _b !== void 0 ? _b : []).length;
        }
        else {
            newParentId = (_c = maps.parentById.get(hoveredId)) !== null && _c !== void 0 ? _c : null;
            const siblings = (_d = maps.childrenByParentId.get(newParentId)) !== null && _d !== void 0 ? _d : [];
            const targetIndex = siblings.findIndex((sib) => sib.id === hoveredId);
            newPosition = dropPosition === 'before' ? targetIndex : targetIndex + 1;
        }
        // Build the position-update list (new parent's siblings + old parent's
        // siblings when moving between containers).
        const positionUpdates = [];
        // Start with the dragged item itself.
        positionUpdates.push({
            id: draggedId,
            parentId: newParentId,
            position: newPosition
        });
        const newParentSiblings = ((_e = maps.childrenByParentId.get(newParentId)) !== null && _e !== void 0 ? _e : [])
            .filter((sib) => sib.id !== draggedId)
            .slice()
            .sort((a, b) => { var _a, _b; return ((_a = a.position) !== null && _a !== void 0 ? _a : 0) - ((_b = b.position) !== null && _b !== void 0 ? _b : 0); });
        newParentSiblings.forEach((sibling, index) => {
            const adjusted = index >= newPosition ? index + 1 : index;
            if (adjusted !== sibling.position) {
                positionUpdates.push({
                    id: sibling.id,
                    parentId: newParentId,
                    position: adjusted
                });
            }
        });
        const oldParentId = (_f = maps.parentById.get(draggedId)) !== null && _f !== void 0 ? _f : null;
        if (oldParentId !== newParentId) {
            const oldParentSiblings = ((_g = maps.childrenByParentId.get(oldParentId)) !== null && _g !== void 0 ? _g : [])
                .filter((sib) => sib.id !== draggedId)
                .slice()
                .sort((a, b) => { var _a, _b; return ((_a = a.position) !== null && _a !== void 0 ? _a : 0) - ((_b = b.position) !== null && _b !== void 0 ? _b : 0); });
            oldParentSiblings.forEach((sibling, index) => {
                if (sibling.position !== index) {
                    positionUpdates.push({
                        id: sibling.id,
                        parentId: oldParentId,
                        position: index
                    });
                }
            });
        }
        // Reconstruct a TreeNode shape for the result (depth + flags used by
        // consumers). Depth lookup walks up the parent chain — O(depth).
        let depth = 0;
        let cursor = draggedId;
        const visited = new Set();
        while (cursor !== null && cursor !== undefined && !visited.has(cursor)) {
            visited.add(cursor);
            const parent = maps.parentById.get(cursor);
            if (parent === null || parent === undefined)
                break;
            depth += 1;
            cursor = parent;
        }
        // Build the TreeNode from the known fields — we explicitly do not
        // include the nested `children` TreeItem[] because TreeNode requires
        // each child to carry its own `depth`, which isn't worth recomputing
        // here (consumers only read id/data/depth/flags).
        const draggedNode = {
            id: draggedItem.id,
            parentId: draggedItem.parentId,
            position: draggedItem.position,
            data: draggedItem.data,
            isDragDisabled: draggedItem.isDragDisabled,
            hideAddButton: draggedItem.hideAddButton,
            addDisabledReason: draggedItem.addDisabledReason,
            depth,
            isDragging: true,
            hasChildren: ((_h = maps.childrenByParentId.get(draggedId)) !== null && _h !== void 0 ? _h : []).length > 0
        };
        finalize();
        cb({
            draggedItem: draggedNode,
            newParentId,
            newPosition,
            positionUpdates
        });
    }, [store]);
    return {
        handleContainerDragStart,
        handleContainerDragOver,
        handleContainerDragEnd,
        handleContainerDrop,
        handleContainerDragLeave,
        lookupMaps
    };
}
